import { globalConfig } from "config";
import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Paths from "routes/Paths";

import {
	ForgotPasswordRoute,
	LoginRoute,
	RegisterRoute,
	ResetPasswordRoute,
	VerifyEmailRoute
} from "./routes/AccountRoutes";
import ContactRoute from "./routes/ContactRoute";
import ExploreRoute from "./routes/ExploreRoute";
import HelpSupportRoute from "./routes/HelpSupportRoute";
import LandingRoute from "./routes/LandingRoute";
import {
	CookiePolicyRoute,
	PrivacyPolicyRoute,
	RefundPolicyRoute,
	RightsPolicyRoute,
	TermsOfServiceRoute
} from "./routes/LegalRoutes";
import NewsletterUnsubscribeRoute from "./routes/NewsletterUnsubscribeRoute";
import NotFoundRoute from "./routes/NotFoundRoute";
import PricingRoute from "./routes/PricingRoute";
import SignInRedirectRoute from "./routes/SignInRedirectRoute";
import TreeFullViewRoute from "./routes/TreeFullViewRoute";
import TreeViewRoute from "./routes/TreeViewRoute";
import UserProfileRoute from "./routes/UserProfileRoute";

const PublicRouter = () => {
	// Signed-in-only pages (e.g. /settings after signing out) land on the homepage instead of the 404 page.
	const rootRedirectElement = <Navigate to={Paths.root} replace />;
	// Tree pages support anonymous viewing of public trees, but the feature flag keeps them behind
	// sign-in for now; the sign-in page returns the visitor to the tree they came for.
	const treeViewElement = globalConfig.features.allowAnonymousTreeViewing ? (
		<TreeViewRoute />
	) : (
		<SignInRedirectRoute />
	);
	const treeFullViewElement = globalConfig.features.allowAnonymousTreeViewing ? (
		<TreeFullViewRoute />
	) : (
		<SignInRedirectRoute />
	);
	// Public profiles follow the same anonymous-viewing gate as public trees.
	const userProfileElement = globalConfig.features.allowAnonymousTreeViewing ? (
		<UserProfileRoute />
	) : (
		<SignInRedirectRoute />
	);

	return (
		<Routes>
			<Route element={<LandingRoute />} path={Paths.root} />
			{/* Explore is the only page that lists public trees. Gating it left crawlers — and any
			    signed-out reader — with no route to a tree at all, so it renders anonymously and the
			    API returns the public pool. */}
			<Route element={<ExploreRoute />} path={Paths.explore} />
			<Route element={rootRedirectElement} path={Paths.notifications} />
			<Route element={rootRedirectElement} path={Paths.sharedWithMe} />
			{/* Pricing must be readable without an account: signed-out visitors get the public pricing
			    page here, signed-in users get the account-aware plans page via PrivateRouter. */}
			<Route element={<PricingRoute />} path={Paths.plans} />
			<Route element={rootRedirectElement} path={Paths.settings} />
			<Route element={<SignInRedirectRoute />} path={Paths.treeEditPattern} />
			<Route element={treeViewElement} path={Paths.treeViewPattern} />
			<Route element={treeFullViewElement} path={Paths.treeFullViewPattern} />
			<Route element={userProfileElement} path={Paths.userProfilePattern} />
			<Route element={<LoginRoute />} path={Paths.signIn} />
			<Route element={<RegisterRoute />} path={Paths.signUp} />
			<Route element={<ForgotPasswordRoute />} path={Paths.forgotPassword} />
			<Route element={<ResetPasswordRoute />} path={Paths.resetPassword} />
			<Route element={<VerifyEmailRoute />} path={Paths.verifyEmail} />
			<Route element={<HelpSupportRoute />} path={Paths.helpSupport} />
			<Route element={<ContactRoute />} path={Paths.contact} />
			<Route element={<PrivacyPolicyRoute />} path={Paths.privacyPolicy} />
			<Route element={<TermsOfServiceRoute />} path={Paths.termsOfService} />
			<Route element={<RefundPolicyRoute />} path={Paths.refundPolicy} />
			<Route element={<CookiePolicyRoute />} path={Paths.cookiePolicy} />
			<Route element={<RightsPolicyRoute />} path={Paths.rightsPolicy} />
			<Route element={<NewsletterUnsubscribeRoute />} path={Paths.newsletterUnsubscribe} />
			<Route element={<NotFoundRoute />} path={Paths.notFound} />
		</Routes>
	);
};

export default PublicRouter;
